import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getUrl, list, getProperties, uploadData, copy } from 'aws-amplify/storage';
import { useAuth } from '../context/AuthContext';
import useCache, { clearCacheByPrefix } from '../hooks/useCache';
import { useCacheContext } from '../context/CacheContext';
import CacheIndicator from './CacheIndicator';
import ChatInterface from './ChatInterface';
import RecordingDetails from './RecordingDetails';
import RecentView from './RecentView';
import SearchView from './SearchView';
import AIAssistantView from './AIAssistantView';
import TrialBanner from './TrialBanner';
import SettingsView from './SettingsView';
import s3Service from '../services/s3Service';
import knowledgeBaseService from '../services/knowledgeBaseService';
import config from '../config';
import {
  AppLayout,
  BreadcrumbGroup,
  Container,
  Header,
  SideNavigation,
  Table,
  Box,
  SpaceBetween,
  Button,
  TextFilter,
  Link,
  Spinner,
  Alert,
  Icon,
  ButtonDropdown,
  Modal,
  Input,
  FormField,
  Select,
  Badge
} from '@cloudscape-design/components';

const S3_PAGE_SIZE = 100;
const DISPLAY_INCREMENT = 10;

const filterAndSortItems = (items) => {
  const webmAndFolders = items.filter(item => 
    item.type === 'folder' || item.name.toLowerCase().endsWith('.webm')
  );
  
  return webmAndFolders.sort((a, b) => {
    if (a.type === 'folder' && b.type !== 'folder') return -1;
    if (a.type !== 'folder' && b.type === 'folder') return 1;
    
    if (a.type === 'folder' && b.type === 'folder') {
      return a.name.localeCompare(b.name);
    } else {
      // Sort files by creation timestamp (newest first), fall back to lastModified
      const dateA = new Date(a.timestamp || a.lastModified);
      const dateB = new Date(b.timestamp || b.lastModified);
      return dateB - dateA;
    }
  });
};

const Recordings = () => {
  // Get auth context
  const { isAuthenticated } = useAuth();
  const { setActiveCacheKey } = useCacheContext();
  
  // State for S3 objects and navigation
  const [error, setError] = useState(null);
  const [userHomePath, setUserHomePath] = useState(() => {
    return localStorage.getItem('recapify_identityId') || '';
  });
  const [currentPath, setCurrentPath] = useState('');
  const [breadcrumbs, setBreadcrumbs] = useState([{ text: 'Root', href: '#' }]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [filteringText, setFilteringText] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [createFolderModalVisible, setCreateFolderModalVisible] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [moveFileModalVisible, setMoveFileModalVisible] = useState(false);
  const [targetPath, setTargetPath] = useState('');
  const [isMovingFile, setIsMovingFile] = useState(false);
  const [availableFolders, setAvailableFolders] = useState([]);
  const [renameModalVisible, setRenameModalVisible] = useState(false);
  const [newFileName, setNewFileName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [visibleCount, setVisibleCount] = useState(DISPLAY_INCREMENT);
  
  // State for video player and transcript
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [navigationOpen, setNavigationOpen] = useState(true);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [viewMode, setViewMode] = useState('folders');
  const [showRecordingDetails, setShowRecordingDetails] = useState(false);
  const [previousViewMode, setPreviousViewMode] = useState('folders');

  // Track which cache key is active so the top nav refresh button targets the right cache
  useEffect(() => {
    if (viewMode === 'folders') {
      setActiveCacheKey(`recordings_${userHomePath}_${currentPath}`);
    } else if (viewMode === 'recent') {
      setActiveCacheKey(`recent-${userHomePath}`);
    } else {
      setActiveCacheKey(null);
    }
  }, [viewMode, userHomePath, currentPath, setActiveCacheKey]);

  // Helper function to get full S3 path
  const getFullPath = (filename = '') => {
    const parts = [userHomePath, currentPath, filename]
      .filter(Boolean)
      .map(part => part.replace(/^\/+|\/+$/g, '')); // Remove leading/trailing slashes
    return parts.join('/');
  };

  // Invalidate all recording-related caches (recordings + recent view)
  const invalidateRecordingCaches = () => {
    clearCacheByPrefix('recordings_');
    clearCacheByPrefix('recent-');
  };

  // Update the recordings index DynamoDB table via Lambda
  const updateRecordingsIndex = async (action, payload) => {
    if (!config.RecordingsIndexFunctionArn) return;
    try {
      const { LambdaClient, InvokeCommand } = await import('@aws-sdk/client-lambda');
      const session = await fetchAuthSession();
      const lambda = new LambdaClient({
        region: config.Region,
        credentials: session.credentials
      });
      const command = new InvokeCommand({
        FunctionName: config.RecordingsIndexFunctionArn,
        Payload: JSON.stringify({ action, ...payload })
      });
      await lambda.send(command);
    } catch (err) {
      console.error('Error updating recordings index:', err);
      // Non-blocking — don't fail the user action
    }
  };

  // Set user home path on component mount
  useEffect(() => {
    const setHomePath = async () => {
      try {
        const session = await fetchAuthSession();
        const identityId = session.identityId;
        setUserHomePath(identityId);
        localStorage.setItem('recapify_identityId', identityId);
      } catch (err) {
        console.error('Error getting identity:', err);
      }
    };
    
    if (isAuthenticated) {
      setHomePath();
    }
  }, [isAuthenticated]);

  // Fetch initial batch of S3 objects — loop until we have at least DISPLAY_INCREMENT visible items
  const fetchS3ObjectsFromS3 = async () => {
    const pathPrefix = currentPath ? `${userHomePath}/${currentPath}` : `${userHomePath}/`;
    
    let allProcessedItems = [];
    let nextToken = null;
    
    do {
      const s3Response = await list({
        path: pathPrefix,
        options: { pageSize: S3_PAGE_SIZE, ...(nextToken ? { nextToken } : {}) }
      });
      
      const processedItems = await processS3Response(s3Response);
      allProcessedItems = [...allProcessedItems, ...processedItems];
      nextToken = s3Response.nextToken || null;
      
      const visibleCount = filterAndSortItems(allProcessedItems).length;
      if (visibleCount >= DISPLAY_INCREMENT || !nextToken) break;
    } while (true);
    
    // Dedupe by key
    const deduped = allProcessedItems.filter(
      (item, index, arr) => arr.findIndex(i => i.key === item.key) === index
    );
    
    return { items: deduped, nextToken, visibleCount: DISPLAY_INCREMENT };
  };

  const cacheKey = `recordings_${userHomePath}_${currentPath}`;
  const { data: cacheData, loading: isLoading, error: cacheError, cachedAt, isCached, refresh, update: updateCache } = useCache(
    cacheKey, fetchS3ObjectsFromS3, { enabled: !!(isAuthenticated && userHomePath) }
  );

  // Derive S3 state from cache data
  const [extraItems, setExtraItems] = useState([]);
  const [s3NextToken, setS3NextToken] = useState(null);
  const [hasMoreS3Items, setHasMoreS3Items] = useState(true);

  // Combine cached items with any extra paginated items
  const allS3Items = (() => {
    const base = cacheData?.items || [];
    if (extraItems.length === 0) return base;
    const combined = [...base, ...extraItems];
    return combined.filter((item, index, arr) => arr.findIndex(i => i.key === item.key) === index);
  })();

  // Effective items for display — use filteredItems if populated, otherwise derive from allS3Items
  const allSortedItems = filteredItems.length > 0 || allS3Items.length === 0
    ? filteredItems
    : filterAndSortItems(allS3Items);
  const displayItems = allSortedItems.slice(0, visibleCount);
  const hasMoreToShow = visibleCount < allSortedItems.length || hasMoreS3Items;

  useEffect(() => {
    if (cacheData) {
      setS3NextToken(cacheData.nextToken);
      setHasMoreS3Items(!!cacheData.nextToken);
      setExtraItems([]); // Reset extra items when cache key changes
      setVisibleCount(cacheData.visibleCount || DISPLAY_INCREMENT);
      updateFilteredItems(cacheData.items || []);
      updateBreadcrumbs();
    }
  }, [cacheData]);

  // Show more items — reveals already-loaded items first, fetches from S3 if needed
  const loadMoreItems = async () => {
    const newVisibleCount = visibleCount + DISPLAY_INCREMENT;
    
    // If we need to fetch more from S3, do that first before bumping visible count
    if (newVisibleCount >= allSortedItems.length && s3NextToken && !isLoadingMore) {
      setIsLoadingMore(true);
      try {
        const pathPrefix = currentPath ? `${userHomePath}/${currentPath}` : `${userHomePath}/`;
        
        let newExtra = [...extraItems];
        let currentNextToken = s3NextToken;
        
        // Loop until we have enough visible items to fill the new visible count
        do {
          const s3Response = await list({
            path: pathPrefix,
            options: { 
              pageSize: S3_PAGE_SIZE,
              nextToken: currentNextToken
            }
          });
          
          const processedItems = await processS3Response(s3Response);
          newExtra = [...newExtra, ...processedItems];
          currentNextToken = s3Response.nextToken || null;
          
          // Check if we have enough visible items
          const base = cacheData?.items || [];
          const combined = [...base, ...newExtra];
          const deduped = combined.filter((item, index, arr) => arr.findIndex(i => i.key === item.key) === index);
          const totalVisible = filterAndSortItems(deduped).length;
          
          if (totalVisible >= newVisibleCount || !currentNextToken) break;
        } while (true);
        
        setExtraItems(newExtra);
        setS3NextToken(currentNextToken);
        setHasMoreS3Items(!!currentNextToken);
        
        // Combine with base items and re-sort everything for proper ordering
        const base = cacheData?.items || [];
        const combined = [...base, ...newExtra];
        const deduped = combined.filter((item, index, arr) => arr.findIndex(i => i.key === item.key) === index);
        updateFilteredItems(deduped);
        
        // Update cache with all loaded items and visible count
        updateCache({ items: deduped, nextToken: currentNextToken, visibleCount: newVisibleCount });
      } catch (err) {
        console.error('Error loading more S3 objects:', err);
        setError('Failed to load more files.');
      } finally {
        setIsLoadingMore(false);
      }
    } else {
      // No S3 fetch needed — just persist the new visible count to cache
      const items = cacheData?.items || [];
      updateCache({ ...cacheData, items, visibleCount: newVisibleCount });
    }
    
    // Bump visible count after data is ready
    setVisibleCount(newVisibleCount);
  };

  // Poll status for incomplete items
  // const pollIncompleteItems = async () => {
  //   const incompleteItems = allS3Items.filter(item => {
  //     if (!item.isVideo || !item.status) return false;
  //     const statusLower = item.status.toLowerCase();
  //     // Stop polling if completed or if Error is present
  //     return statusLower !== 'completed' && !statusLower.includes('error');
  //   });
    
  //   if (incompleteItems.length === 0) return;
    
  //   for (const item of incompleteItems) {
  //     try {
  //       // Get status from tags
  //       const { S3Client, GetObjectTaggingCommand } = await import('@aws-sdk/client-s3');
  //       const { fetchAuthSession } = await import('aws-amplify/auth');
  //       const config = await import('../config.js');
        
  //       const session = await fetchAuthSession();
  //       const s3Client = new S3Client({
  //         region: config.default.Region,
  //         credentials: session.credentials
  //       });
        
  //       const taggingResponse = await s3Client.send(new GetObjectTaggingCommand({
  //         Bucket: config.default.Bucket,
  //         Key: item.key
  //       }));
        
  //       const tags = {};
  //       taggingResponse.TagSet?.forEach(tag => {
  //         tags[tag.Key] = tag.Value;
  //       });
        
  //       const newStatus = tags.status || 'Error';
        
  //       if (newStatus !== item.status) {
  //         // Update only the specific item to avoid full table re-render
  //         item.status = newStatus;
          
  //         // Force re-render of filtered items with updated reference
  //         setFilteredItems(prev => [...prev]);
  //       }
  //     } catch (err) {
  //       console.error(`Error polling status for ${item.key}:`, err);
  //     }
  //   }
  // };

  // Set up status polling - DISABLED for now
  // useEffect(() => {
  //   const hasIncompleteItems = allS3Items.some(item => {
  //     if (!item.isVideo || !item.status) return false;
  //     const statusLower = item.status.toLowerCase();
  //     // Stop polling if completed or if Error is present
  //     return statusLower !== 'completed' && !statusLower.includes('error');
  //   });
  //   
  //   if (hasIncompleteItems && !statusPollingInterval) {
  //     const interval = setInterval(pollIncompleteItems, 5000);
  //     setStatusPollingInterval(interval);
  //   } else if (!hasIncompleteItems && statusPollingInterval) {
  //     clearInterval(statusPollingInterval);
  //     setStatusPollingInterval(null);
  //   }
  //   
  //   return () => {
  //     if (statusPollingInterval) {
  //       clearInterval(statusPollingInterval);
  //     }
  //   };
  // }, [allS3Items]);
  const updateFilteredItems = (items) => {
    setFilteredItems(filterAndSortItems(items));
  };

  // Delete file or folder
  const handleDelete = async () => {
    if (!itemToDelete) return;
    
    setIsDeleting(true);
    try {
      // If it's a folder, check if it's empty before deleting
      if (itemToDelete.type === 'folder') {
        // List all items in the folder
        const folderPath = itemToDelete.key.endsWith('/') ? itemToDelete.key : itemToDelete.key + '/';
        const folderContents = await list({
          path: folderPath,
          options: { listAll: true }
        });
        
        // Check if folder has any .webm files
        const hasWebmFiles = folderContents.items.some(item => 
          item.path.toLowerCase().endsWith('.webm')
        );
        
        if (hasWebmFiles) {
          setError('Cannot delete folder that contains recordings. Please delete all recordings first.');
          return;
        }
        
        // Delete all remaining files in the folder (including .folder placeholder)
        for (const item of folderContents.items) {
          await s3Service.deleteObject(config.Bucket, item.path);
        }
      } else if (itemToDelete.isVideo) {
        // If it's a video file, check status and delete all related files (.webm, .json, .md)
        // Check if file is completed, has error, or has no status (use cached status from item)
        const status = itemToDelete.status?.toLowerCase();
        if (status && status !== 'completed' && !status.includes('error')) {
          setError('This file is currently being processed. Please try deleting it again later.');
          return;
        }
        
        const webmPath = itemToDelete.key;
        const extensions = ['.webm', '.json', '.md'];
        const filesToDelete = [];
        
        // Check each related file by replacing the .webm extension
        for (const ext of extensions) {
          const filePath = webmPath.replace('.webm', ext);
          
          try {
            await getProperties({ path: filePath });
            filesToDelete.push(filePath);
          } catch (err) {
            // File doesn't exist, skip
          }
        }
        
        // Delete all related files from main bucket
        for (const filePath of filesToDelete) {
          await s3Service.deleteObject(config.Bucket, filePath);
        }
        
        // Delete corresponding KB files (blocking for consistency)
        await knowledgeBaseService.deleteKBFiles(webmPath);
      } else {
        // For non-video files, just delete the single file
        await s3Service.deleteObject(config.Bucket, itemToDelete.key);
      }
      
      // Update recordings index (delete entry for video files)
      if (itemToDelete.isVideo) {
        await updateRecordingsIndex('deleteItem', { s3Key: itemToDelete.key });
      }
      
      // Refresh the file list from S3
      invalidateRecordingCaches();
      await refresh();
      
      // Clear selection if the deleted item was selected
      if (selectedItems.some(item => item.key === itemToDelete.key)) {
        setSelectedItems([]);
        setSelectedVideo(null);
        setShowRecordingDetails(false);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error deleting item:', err);
      setError(`Failed to delete ${itemToDelete.name}. Please try again later.`);
    } finally {
      setIsDeleting(false);
      setDeleteModalVisible(false);
      setItemToDelete(null);
    }
  };

  // Show delete confirmation modal
  const showDeleteConfirmation = () => {
    if (selectedItems.length !== 1) {
      setError('Please select a single item to delete.');
      return;
    }
    
    setItemToDelete(selectedItems[0]);
    setDeleteModalVisible(true);
  };

  // Create folder function
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      setError('Please enter a folder name.');
      return;
    }

    setIsCreatingFolder(true);
    try {
      // Create an empty file to represent the folder
      // Create an empty file to represent the folder
      await uploadData({
        path: getFullPath(`${newFolderName.trim()}/.folder`),
        data: new Blob([''], { type: 'text/plain' })
      });

      // Refresh the file list
      invalidateRecordingCaches();
      await refresh();
      
      setCreateFolderModalVisible(false);
      setNewFolderName('');
      setError(null);
    } catch (err) {
      console.error('Error creating folder:', err);
      setError('Failed to create folder. Please try again.');
    } finally {
      setIsCreatingFolder(false);
    }
  };

  // Rename file function
  const handleRenameFile = async () => {
    if (selectedItems.length !== 1 || !newFileName.trim()) return;
    
    const fileToRename = selectedItems[0];
    const newDisplayName = newFileName.trim();
    
    setIsRenaming(true);
    try {
      // Get current metadata
      const properties = await getProperties({ path: fileToRename.key });
      const currentMetadata = properties.metadata || {};
      
      // Update metadata with new display name
      const updatedMetadata = {
        ...currentMetadata,
        'display-name': newDisplayName
      };
      
      // Use AWS SDK directly for metadata update
      const { S3Client, CopyObjectCommand } = await import('@aws-sdk/client-s3');
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const config = await import('../config.js');
      
      const session = await fetchAuthSession();
      const s3Client = new S3Client({
        region: config.default.Region,
        credentials: session.credentials
      });
      
      await s3Client.send(new CopyObjectCommand({
        Bucket: config.default.Bucket,
        CopySource: `${config.default.Bucket}/${fileToRename.key}`,
        Key: fileToRename.key,
        Metadata: updatedMetadata,
        MetadataDirective: 'REPLACE'
      }));
      
      // Update recordings index with new display name
      await updateRecordingsIndex('updateItem', {
        oldS3Key: fileToRename.key,
        updates: { displayName: newDisplayName }
      });
      
      // Refresh the file list
      invalidateRecordingCaches();
      await refresh();
      
      setRenameModalVisible(false);
      setNewFileName('');
      setSelectedItems([]);
      setError(null);
    } catch (err) {
      console.error('Error renaming file:', err);
      setError('Failed to rename file. Please try again.');
    } finally {
      setIsRenaming(false);
    }
  };

  // Move file function
  const handleMoveFile = async () => {
    if (selectedItems.length !== 1) return;
    
    const fileToMove = selectedItems[0];
    
    // Check if trying to move to the same location
    const currentFolder = fileToMove.key.includes('/') ? fileToMove.key.substring(0, fileToMove.key.lastIndexOf('/')) : '';
    const targetFolder = targetPath ? `${userHomePath}/${targetPath}` : userHomePath;
    
    if (currentFolder === targetFolder) {
      setError('File is already in the selected location.');
      setMoveFileModalVisible(false);
      setTargetPath('');
      return;
    }
    
    // Check if file is completed, has error, or has no status (use cached status from item)
    const status = fileToMove.status?.toLowerCase();
    if (status && status !== 'completed' && !status.includes('error')) {
      setError('This file is currently being processed. Please try moving it again later.');
      return;
    }
    
    setIsMovingFile(true);
    try {
      // Get the webm file path and replace extension for related files
      const webmPath = fileToMove.key;
      const extensions = ['.webm', '.json', '.md'];
      const filesToMove = [];
      
      // Check each related file by replacing the .webm extension
      for (const ext of extensions) {
        const sourcePath = webmPath.replace('.webm', ext);
        const fileName = sourcePath.split('/').pop();
        const targetFilePath = targetPath ? `${userHomePath}/${targetPath}/${fileName}` : `${userHomePath}/${fileName}`;
        
        try {
          await getProperties({ path: sourcePath });
          filesToMove.push({ sourcePath, targetPath: targetFilePath });
        } catch (err) {
          // File doesn't exist, skip
        }
      }
      
      // Copy all files first
      for (const file of filesToMove) {
        await copy({
          source: { path: file.sourcePath },
          destination: { path: file.targetPath }
        });
      }
      
      // Only delete originals if all copies succeeded
      for (const file of filesToMove) {
        await s3Service.deleteObject(config.Bucket, file.sourcePath);
      }
      
      // Update KB file tags with new recording path (blocking for consistency)
      const newWebmPath = filesToMove.find(f => f.sourcePath.endsWith('.webm'))?.targetPath;
      if (newWebmPath) {
        await knowledgeBaseService.updateKBFileTags(webmPath, newWebmPath);
      }
      
      // Update recordings index with new S3 key
      if (newWebmPath) {
        await updateRecordingsIndex('updateItem', {
          oldS3Key: webmPath,
          updates: { s3Key: newWebmPath }
        });
      }
      
      // Refresh the file list
      invalidateRecordingCaches();
      await refresh();
      
      setMoveFileModalVisible(false);
      setTargetPath('');
      setSelectedItems([]);
      setError(null);
    } catch (err) {
      console.error('Error moving files:', err);
      if (err.message && err.message.includes('copy an object to itself')) {
        setError('File is already in the selected location.');
        setMoveFileModalVisible(false);
        setTargetPath('');
      } else {
        setError('Failed to move files. Please try again.');
      }
    } finally {
      setIsMovingFile(false);
    }
  };

  // Helper function to get tags for a file
  const getFileTags = async (key) => {
    return s3Service.getTags(config.Bucket, key);
  };

  // Helper function to determine if a file is a video
  const isVideoFile = (filename) => {
    const videoExtensions = ['.webm', '.mp4', '.avi', '.mov', '.wmv', '.flv', '.mkv'];
    const extension = filename.toLowerCase().substring(filename.lastIndexOf('.'));
    return videoExtensions.includes(extension);
  };

  // Process S3 response to create folder structure
  const processS3Response = async (response) => {
    const items = [];
    const folders = new Set();
    
    // Process items and collect video files that need metadata
    const videoFilesToProcess = [];
    
    response.items.forEach(item => {
      const path = item.path;
      
      if (!path.startsWith(userHomePath + '/')) return;
      
      // Remove userHomePath prefix to get relative path
      const relativePath = path.substring(userHomePath.length + 1);
      
      // Check if this item is in the current directory
      const expectedPrefix = currentPath ? currentPath.replace(/\/$/, '') + '/' : '';
      
      if (!relativePath.startsWith(expectedPrefix)) return;
      
      // Get the part after current path
      const remainingPath = relativePath.substring(expectedPrefix.length);
      
      if (!remainingPath) return;
      
      const pathParts = remainingPath.split('/');
      const firstPart = pathParts[0];
      
      // If it's a direct child
      if (pathParts.length === 1) {
        // It's a file
        const fileItem = {
          name: firstPart,
          key: path,
          type: 'file',
          lastModified: item.lastModified,
          size: item.size,
          isVideo: isVideoFile(firstPart)
        };
        
        // If it's a video file, we'll fetch metadata later
        if (fileItem.isVideo) {
          videoFilesToProcess.push(fileItem);
        } else {
          items.push(fileItem);
        }
      } else if (pathParts.length === 2 && pathParts[1] === '.folder') {
        // It's a folder marker
        folders.add(firstPart);
      } else if (pathParts.length > 1) {
        // It's in a subfolder
        folders.add(firstPart);
      }
    });
    
    // Fetch metadata and tags for video files in parallel
    await Promise.all(videoFilesToProcess.map(async (videoItem) => {
      try {
        const [result, tags] = await Promise.all([
          getProperties({ path: videoItem.key }),
          getFileTags(videoItem.key)
        ]);
        
        const displayName = result.metadata?.['display-name'];
        const timestamp = result.metadata?.timestamp;
        const status = tags.status || 'Error';
        
        videoItem.displayName = displayName || videoItem.name;
        videoItem.status = status;
        videoItem.timestamp = timestamp;
        items.push(videoItem);
      } catch (err) {
        console.error(`Error fetching properties/tags for ${videoItem.key}:`, err);
        videoItem.displayName = videoItem.name;
        videoItem.status = 'Error';
        items.push(videoItem);
      }
    }));
    
    // Add folders to items
    folders.forEach(folderName => {
      items.push({
        name: folderName,
        key: getFullPath(folderName),
        type: 'folder',
        lastModified: null,
        size: null,
        isVideo: false
      });
    });
    
    // Sort items: folders first (alphabetically), then files (by creation timestamp, newest first)
    const sortedItems = items.sort((a, b) => {
      if (a.type === 'folder' && b.type !== 'folder') return -1;
      if (a.type !== 'folder' && b.type === 'folder') return 1;
      
      if (a.type === 'folder' && b.type === 'folder') {
        return a.name.localeCompare(b.name);
      } else {
        // Sort files by creation timestamp (newest first), fall back to lastModified
        const dateA = new Date(a.timestamp || a.lastModified);
        const dateB = new Date(b.timestamp || b.lastModified);
        return dateB - dateA;
      }
    });
    
    console.log("S3 response sorted:", sortedItems);
    return sortedItems;
  };

  // Update breadcrumbs based on current path
  const updateBreadcrumbs = () => {
    const pathParts = currentPath.split('/').filter(Boolean);
    const newBreadcrumbs = [{ text: 'Root', href: '#', key: 'root' }];
    
    let cumulativePath = '';
    pathParts.forEach((part, index) => {
      cumulativePath += part + '/';
      // For .webm files, try to find the display name from allS3Items
      let displayText = part;
      if (part.endsWith('.webm')) {
        const matchingObject = allS3Items.find(obj => obj.key.endsWith(part));
        if (matchingObject && matchingObject.displayName) {
          displayText = matchingObject.displayName;
        }
      }
      newBreadcrumbs.push({
        text: displayText,
        href: '#' + cumulativePath,
        key: `breadcrumb-${index}`
      });
    });
    
    setBreadcrumbs(newBreadcrumbs);
  };

  // Navigate to a folder
  const navigateToFolder = (folderKey) => {
    console.log(`Navigating to folder: ${folderKey}`);
    // Remove userHomePath prefix to get relative path
    const relativePath = folderKey.replace(`${userHomePath}/`, '');
    setCurrentPath(relativePath);
    setSelectedItems([]);
  };

  // Handle item selection
  const handleItemSelection = ({ detail }) => {
    setSelectedItems(detail.selectedItems);
    
    // Only clear video if selection is cleared, don't auto-open on selection
    if (detail.selectedItems.length === 0) {
      setSelectedVideo(null);
      setShowRecordingDetails(false);
    }
  };

  // Load video and show video viewer
  const selectVideo = async (videoItem) => {
    setPreviousViewMode(viewMode); // Save current view mode
    setSelectedVideo(videoItem);
    setShowRecordingDetails(true);
    setViewMode('folders'); // Switch to folders view to display RecordingDetails
    
    // Make sure the item is selected in the table
    setSelectedItems([videoItem]);
  };

  // Handle back from video viewer
  const handleBackFromVideo = () => {
    setShowRecordingDetails(false);
    setSelectedVideo(null);
    setViewMode(previousViewMode); // Restore previous view mode
  };

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes === null || bytes === undefined) return '';
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleString('sv-SE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Column definitions for the table
  const columnDefinitions = [
    {
      id: 'name',
      header: 'Name',
      cell: item => {
        if (item.type === 'folder') {
          return (
            <Link href="#" onFollow={() => navigateToFolder(item.key)}>
              <Icon name="folder" /> {item.name}
            </Link>
          );
        } else if (item.isVideo) {
          return (
            <Link href="#" onFollow={() => selectVideo(item)}>
              <Icon name="video" /> {item.displayName}
            </Link>
          );
        } else {
          return <span><Icon name="file" /> {item.displayName}</span>;
        }
      },
      sortingField: 'name',
      isRowHeader: true,
      width: '100%'
    },
    {
      id: 'type',
      header: 'Type',
      cell: item => item.type === 'folder' ? 'Folder' : 
                    item.isVideo ? 'Video' : 'File',
      sortingField: 'type',
      width: 'auto'
    },
    {
      id: 'status',
      header: 'Status',
      minWidth: 250,
      cell: item => {
        if (item.isVideo && item.status) {
          // Parse comma-separated statuses
          const statuses = item.status.split(',').map(s => s.trim());
          const statusLower = item.status.toLowerCase();
          const isPolling = statusLower !== 'completed' && !statusLower.includes('error');
          
          const badges = statuses.map((status, index) => {
            const statusLower = status.toLowerCase();
            return (
              <Badge key={index} color={
                statusLower === 'completed' ? 'green' :
                statusLower === 'uploaded' ? 'blue' :
                statusLower === 'transcribing' ? 'blue' :
                statusLower === 'generating ai summary' ? 'blue' :
                statusLower === 'error' ? 'red' :
                'grey'
              }>
                {status}
              </Badge>
            );
          });
          
          return (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
              {badges}
            </div>
          );
        }
        return '';
      },
      sortingField: 'status'
    },
    {
      id: 'size',
      header: 'Size',
      cell: item => formatFileSize(item.size),
      sortingField: 'size',
      width: 'auto'
    },
    {
      id: 'timestamp',
      header: 'Created',
      cell: item => formatDate(item.timestamp),
      sortingField: 'timestamp',
      width: 'auto'
    }
  ];

  // Actions dropdown items
  const actionItems = [
    { id: 'createFolder', text: 'Create Folder' },
    { id: 'rename', text: 'Rename', disabled: selectedItems.length !== 1 || selectedItems[0]?.type === 'folder' },
    { id: 'moveFile', text: 'Move File', disabled: selectedItems.length !== 1 },
    { id: 'delete', text: 'Delete', disabled: selectedItems.length !== 1 }
  ];

  // Handle action selection
  const handleActionClick = ({ detail }) => {
    switch (detail.id) {
      case 'createFolder':
        setCreateFolderModalVisible(true);
        break;
      case 'rename':
        if (selectedItems.length === 1 && selectedItems[0].type !== 'folder') {
          // Remove .webm extension for editing, we'll add it back
          const currentName = selectedItems[0].displayName || selectedItems[0].name;
          const nameWithoutExtension = currentName.replace(/\.webm$/, '');
          setNewFileName(nameWithoutExtension);
          setRenameModalVisible(true);
        }
        break;
      case 'moveFile':
        if (selectedItems.length === 1) {
          // Get all folders from entire user directory
          const loadAllFolders = async () => {
            try {
              const allItems = await list({ 
                path: `${userHomePath}/`,
                options: { listAll: true } 
              });
              
              const allFolders = new Set(['']); // Root folder
              
              allItems.items.forEach(item => {
                if (!item.path.startsWith(userHomePath + '/')) return;
                
                const relativePath = item.path.substring(userHomePath.length + 1);
                const pathParts = relativePath.split('/');
                
                // Build all possible folder paths
                let currentPath = '';
                for (let i = 0; i < pathParts.length - 1; i++) {
                  currentPath += (currentPath ? '/' : '') + pathParts[i];
                  allFolders.add(currentPath);
                }
              });
              
              // Build hierarchical folder structure
              const buildHierarchy = (folders) => {
                const options = [{ label: 'Root', value: '' }];
                
                // Sort folders alphabetically by full path to maintain hierarchy
                const sortedFolders = Array.from(folders)
                  .filter(f => f !== '')
                  .sort((a, b) => a.localeCompare(b));
                
                sortedFolders.forEach(folder => {
                  const parts = folder.split('/');
                  const depth = parts.length;
                  const folderName = parts[parts.length - 1];
                  const prefix = '└─ '.repeat(depth);
                  
                  options.push({
                    label: `${prefix}${folderName}`,
                    value: folder
                  });
                });
                
                return options;
              };
              
              const folderOptions = buildHierarchy(allFolders);
              
              setAvailableFolders(folderOptions);
              setMoveFileModalVisible(true);
            } catch (err) {
              console.error('Error loading all folders:', err);
              setError('Failed to load folders.');
            }
          };
          
          loadAllFolders();
        }
        break;
      case 'delete':
        showDeleteConfirmation();
        break;
      default:
        break;
    }
  };

  // Build breadcrumbs for recording details view
  const getRecordingDetailsBreadcrumbs = () => {
    const items = [{ text: 'Root', href: '#root', key: 'root' }];
    
    if (currentPath) {
      const pathParts = currentPath.split('/').filter(Boolean);
      let cumulativePath = '';
      pathParts.forEach((part, index) => {
        cumulativePath += part + '/';
        items.push({
          text: part,
          href: `#${cumulativePath}`,
          key: `breadcrumb-${index}`
        });
      });
    }
    
    if (selectedVideo) {
      items.push({
        text: selectedVideo.displayName,
        href: '#',
        key: 'current-video'
      });
    }
    
    return items;
  };

  return (
    <AppLayout
      headerSelector=".sticky-nav"
      notifications={<TrialBanner />}
      breadcrumbs={
        (viewMode === 'folders' || viewMode === 'recent' || showRecordingDetails) ? (
          <BreadcrumbGroup
            items={showRecordingDetails ? getRecordingDetailsBreadcrumbs() : breadcrumbs}
            onFollow={e => {
              e.preventDefault();
              const href = e.detail.href;
              if (showRecordingDetails) {
                if (href === '#root') {
                  setCurrentPath('');
                  handleBackFromVideo();
                } else if (href.startsWith('#') && href !== '#') {
                  const folderPath = href.substring(1);
                  setCurrentPath(folderPath);
                  handleBackFromVideo();
                }
              } else {
                if (href === '#') {
                  setCurrentPath('');
                } else {
                  setCurrentPath(href.substring(1)); // Remove the # character
                }
              }
            }}
          />
        ) : undefined
      }
      navigationOpen={navigationOpen}
      onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
      navigation={
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <SideNavigation
          header={{
            href: '#',
            text: 'Library',
          }}
          activeHref={
            viewMode === 'folders' ? '#' :
            viewMode === 'search' ? '#search' :
            viewMode === 'recent' ? '#recent' :
            viewMode === 'settings' ? '#settings' : '#'
          }
          items={[
            { 
              type: 'link', 
              text: (
                <div className={viewMode === 'folders' ? 'nav-item-selected' : 'nav-item'}>
                  <Box padding={{ vertical: 's' }}>
                    <SpaceBetween direction="horizontal" size="s">
                      <Icon name="microphone" size="medium" />
                      <Box fontSize="heading-s" fontWeight={viewMode === 'folders' ? 'bold' : 'normal'}>Recordings</Box>
                    </SpaceBetween>
                  </Box>
                </div>
              ), 
              href: '#'
            },
            { 
              type: 'link', 
              text: (
                <div className={viewMode === 'recent' ? 'nav-item-selected' : 'nav-item'}>
                  <Box padding={{ vertical: 's' }}>
                    <SpaceBetween direction="horizontal" size="s">
                      <Icon name="status-in-progress" size="medium" />
                      <Box fontSize="heading-s" fontWeight={viewMode === 'recent' ? 'bold' : 'normal'}>Recent</Box>
                    </SpaceBetween>
                  </Box>
                </div>
              ), 
              href: '#recent'
            },
            { 
              type: 'link', 
              text: (
                <div className={viewMode === 'search' ? 'nav-item-selected' : 'nav-item'}>
                  <Box padding={{ vertical: 's' }}>
                    <SpaceBetween direction="horizontal" size="s">
                      <Icon name="search" size="medium" />
                      <Box fontSize="heading-s" fontWeight={viewMode === 'search' ? 'bold' : 'normal'}>Search</Box>
                    </SpaceBetween>
                  </Box>
                </div>
              ), 
              href: '#search'
            },
            { 
              type: 'link', 
              text: (
                <div className={viewMode === 'askai' ? 'nav-item-selected' : 'nav-item'}>
                  <Box padding={{ vertical: 's' }}>
                    <SpaceBetween direction="horizontal" size="s">
                      <Icon name="contact" size="medium" />
                      <Box fontSize="heading-s" fontWeight={viewMode === 'askai' ? 'bold' : 'normal'}>AI Assistant</Box>
                    </SpaceBetween>
                  </Box>
                </div>
              ), 
              href: '#askai'
            },
            // Settings view - commented out as transcription method is now per-recording
            // { 
            //   type: 'link', 
            //   text: (
            //     <div className={viewMode === 'settings' ? 'nav-item-selected' : 'nav-item'}>
            //       <Box padding={{ vertical: 's' }}>
            //         <SpaceBetween direction="horizontal" size="s">
            //           <Icon name="settings" size="medium" />
            //           <Box fontSize="heading-s" fontWeight={viewMode === 'settings' ? 'bold' : 'normal'}>Settings</Box>
            //         </SpaceBetween>
            //       </Box>
            //     </div>
            //   ), 
            //   href: '#settings'
            // }
          ]}
          onFollow={e => {
            e.preventDefault();
            const href = e.detail.href;
            // Don't clear recording details or selected video - just change view mode
            // This preserves the state when switching between views
            
            if (href === '#') {
              setViewMode('folders');
            } else if (href === '#search') {
              setViewMode('search');
            } else if (href === '#recent') {
              setViewMode('recent');
            } else if (href === '#askai') {
              setViewMode('askai');
            } else if (href === '#settings') {
              setViewMode('settings');
            }
          }}
        />
        <div style={{ marginTop: 'auto', padding: '16px 20px', borderTop: '1px solid rgba(128,128,128,0.3)', textAlign: 'center' }}>
          <Box padding={{ vertical: 's' }}>
            <Link
              href="https://chromewebstore.google.com/detail/recapify/fkiahkfimfaolnmggkinoebmcmkjbijb"
              external
              fontSize="body-s"
            >
              Get Chrome Extension
            </Link>
          </Box>
        </div>
        </div>
      }
      toolsHide={true}
      maxContentWidth={Number.MAX_VALUE}
      disableContentPaddings={false}
      content={
        <>
          <div style={{ display: showRecordingDetails && selectedVideo && viewMode === 'folders' ? 'block' : 'none' }}>
            {selectedVideo && (
              <RecordingDetails
                video={selectedVideo}
                onBack={handleBackFromVideo}
                backButtonText={
                  previousViewMode === 'search' ? 'Back to Search' :
                  previousViewMode === 'recent' ? 'Back to Recent' :
                  'Back to Recordings'
                }
              />
            )}
          </div>
          <div style={{ display: viewMode === 'search' ? 'block' : 'none' }}>
            <SearchView userHomePath={userHomePath} onVideoSelect={selectVideo} />
          </div>
          <div style={{ display: viewMode === 'recent' ? 'block' : 'none' }}>
            {viewMode === 'recent' && <RecentView userHomePath={userHomePath} onVideoSelect={selectVideo} />}
          </div>
          <div style={{ display: viewMode === 'askai' ? 'block' : 'none' }}>
            <AIAssistantView userHomePath={userHomePath} onVideoSelect={selectVideo} />
          </div>
          {/* Settings view - commented out as transcription method is now per-recording */}
          {/* <div style={{ display: viewMode === 'settings' ? 'block' : 'none' }}>
            <SettingsView userId={userHomePath} />
          </div> */}
          {viewMode === 'folders' && !showRecordingDetails && (
              <Container variant="embedded"
              header={
                <Header
                  variant="h1"
                  description="Browse and play your recorded meetings"
                  actions={<CacheIndicator cachedAt={cachedAt} isCached={isCached} loading={isLoading} onRefresh={refresh} />}
            >
              Meeting Recordings
            </Header>
          }
        >
          {(error || cacheError) && (
            <Alert type="error" dismissible onDismiss={() => setError(null)}>
              {error || cacheError}
            </Alert>
          )}
          
          {isLoading ? (
            <Box textAlign="center" padding="l">
              <Spinner size="large" />
              <Box variant="p" padding={{ top: 's' }}>
                Loading files...
              </Box>
            </Box>
          ) : (
            <>
            <Table
              columnDefinitions={columnDefinitions}
              items={displayItems}
              loading={isLoading}
              loadingText="Loading recordings"
              selectionType="single"
              selectedItems={selectedItems}
              onSelectionChange={handleItemSelection}
              trackBy="key"
              empty={
                <Box textAlign="center" padding="l">
                  <Box fontSize="display-l" padding={{ bottom: 's' }}>🎬</Box>
                  <Box variant="h3" padding={{ bottom: 'xs' }}>No recordings yet</Box>
                  <Box padding={{ top: 's' }}>
                    {currentPath ? (
                      <span>This folder is empty</span>
                    ) : (
                      <SpaceBetween size="m">
                        <Box variant="p" color="text-body-secondary">
                          Install the Chrome Extension to start recording your meetings
                        </Box>
                        <Button
                          variant="primary"
                          iconAlign="left"
                          href="https://chromewebstore.google.com/detail/recapify/fkiahkfimfaolnmggkinoebmcmkjbijb"
                          target="_blank"
                        >
                          Get Chrome Extension
                        </Button>
                      </SpaceBetween>
                    )}
                  </Box>
                </Box>
              }
              filter={
                <TextFilter
                  filteringPlaceholder="Find recordings"
                  filteringText={filteringText}
                  onChange={({ detail }) => setFilteringText(detail.filteringText)}
                  countText={`${displayItems.length} matches`}
                />
              }
              header={
                <Header
                  description={`${displayItems.length} loaded${hasMoreToShow ? ', more available' : ''}`}
                  actions={
                    <SpaceBetween direction="horizontal" size="xs">
                      <ButtonDropdown
                        items={actionItems}
                        onItemClick={handleActionClick}
                      >
                        Actions
                      </ButtonDropdown>
                    </SpaceBetween>
                  }
                >
                  {currentPath ? `Folder: ${currentPath}` : 'All Recordings'}
                </Header>
              }
              stickyHeader
            />
            {hasMoreToShow && (
              <Box textAlign="center" padding={{ top: 'm', bottom: 'm' }}>
                <Button
                  onClick={loadMoreItems}
                  loading={isLoadingMore}
                >
                  Load more recordings
                </Button>
              </Box>
            )}
            </>
          )}
          
          {/* Delete Confirmation Modal */}
          <Modal
            visible={deleteModalVisible}
            onDismiss={() => setDeleteModalVisible(false)}
            header="Confirm deletion"
            closeAriaLabel="Close modal"
            footer={
              <Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="link" onClick={() => setDeleteModalVisible(false)}>
                    Cancel
                  </Button>
                  <Button variant="primary" onClick={handleDelete} loading={isDeleting}>
                    Delete
                  </Button>
                </SpaceBetween>
              </Box>
            }
          >
            <Box variant="span">
              Are you sure you want to delete <b>{itemToDelete?.displayName}</b>?
              <br /><br />
              This action will permanently delete the {itemToDelete?.type} and cannot be undone.
            </Box>
          </Modal>

          {/* Create Folder Modal */}
          <Modal
            visible={createFolderModalVisible}
            onDismiss={() => {
              setCreateFolderModalVisible(false);
              setNewFolderName('');
            }}
            header="Create New Folder"
            closeAriaLabel="Close modal"
            footer={
              <Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button 
                    variant="link" 
                    onClick={() => {
                      setCreateFolderModalVisible(false);
                      setNewFolderName('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="primary" 
                    onClick={handleCreateFolder} 
                    loading={isCreatingFolder}
                    disabled={!newFolderName.trim()}
                  >
                    Create Folder
                  </Button>
                </SpaceBetween>
              </Box>
            }
          >
            <FormField label="Folder name">
              <Input
                value={newFolderName}
                onChange={({ detail }) => setNewFolderName(detail.value)}
                placeholder="Enter folder name"
              />
            </FormField>
          </Modal>

          {/* Rename File Modal */}
          <Modal
            visible={renameModalVisible}
            onDismiss={() => {
              setRenameModalVisible(false);
              setNewFileName('');
            }}
            header="Rename File"
            closeAriaLabel="Close modal"
            footer={
              <Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button 
                    variant="link" 
                    onClick={() => {
                      setRenameModalVisible(false);
                      setNewFileName('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="primary" 
                    onClick={handleRenameFile} 
                    loading={isRenaming}
                    disabled={!newFileName.trim()}
                  >
                    Rename
                  </Button>
                </SpaceBetween>
              </Box>
            }
          >
            <SpaceBetween size="m">
              <Box>
                Renaming: <b>{selectedItems[0]?.displayName || selectedItems[0]?.name}</b>
              </Box>
              <FormField label="New filename (without .webm extension)">
                <Input
                  value={newFileName}
                  onChange={({ detail }) => setNewFileName(detail.value)}
                  placeholder="Enter new filename"
                />
              </FormField>
            </SpaceBetween>
          </Modal>

          {/* Move File Modal */}
          <Modal
            visible={moveFileModalVisible}
            onDismiss={() => {
              setMoveFileModalVisible(false);
              setTargetPath('');
            }}
            header="Move File"
            closeAriaLabel="Close modal"
            footer={
              <Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button 
                    variant="link" 
                    onClick={() => {
                      setMoveFileModalVisible(false);
                      setTargetPath('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="primary" 
                    onClick={handleMoveFile} 
                    loading={isMovingFile}
                  >
                    Move File
                  </Button>
                </SpaceBetween>
              </Box>
            }
          >
            <SpaceBetween size="m">
              <Box>
                Moving: <b>{selectedItems[0]?.displayName}</b>
              </Box>
              <FormField label="Target location">
                <Select
                  selectedOption={availableFolders.find(f => f.value === targetPath) || null}
                  onChange={({ detail }) => setTargetPath(detail.selectedOption.value)}
                  options={availableFolders}
                  placeholder="Select target folder"
                />
              </FormField>
            </SpaceBetween>
          </Modal>
        </Container>
          )}
        </>
      }
    />
  );
};

export default Recordings;
